import ALLOWED_ORIGINS from "@/components/embed/allowed-message-origins";
import { useMessageListener } from "@/components/embed/use-message-listener";
import { setWorkingEvent } from "@/store/actions/admin/create-event";
import { setFallbackEmbedVideo, setSuppressWatermark } from "@/store/actions/event/event-actions";
import { useAppDispatch } from "@/store/reducers/use-typed-selector";
import type { BrandliveEvent } from "@/types/working-model";
import { getLogger } from "@/utils/debug-logger";
import { useEventUuidParamModerator } from "@/utils/use-event-uuid-param";
import { useCallback, useEffect } from "react";
const logger = getLogger('bl-embed:video-data');

/**
 * This component allows us to load the project data before the auth check
 * has completed. This is intentional as the auth check can be slow, and we
 * can prime the project data before auth is finished and helps this load faster.
 */

interface Props {
	children: JSX.Element;
}
const SessionStreamEmbedDataProvider = ({ children }: Props) => {
	const eventUuid = useEventUuidParamModerator();
	const dispatch = useAppDispatch();

	const onWorkingEvent = useCallback((event: BrandliveEvent, fallbackVideo?: string) => {
		logger(`Received event data`, event);
		if (event.uuid === eventUuid) { // working project uuid must match expected from the path
			dispatch(setWorkingEvent(event));
			dispatch(setFallbackEmbedVideo(fallbackVideo));
		}
	}, [dispatch, eventUuid]);

	useEffect(() => {
		for (const origin of ALLOWED_ORIGINS) {
			logger('loaded, sending message to', origin);
			try {
				window.parent.postMessage({ ready: true }, origin);
			} catch (e) {
				logger('Failed to send post message to', origin, e);
			}
		}
	}, []);

	const onSuppressWatermark = useCallback(() => {
		dispatch(setSuppressWatermark());
	}, [dispatch]);

	useMessageListener({
		onWorkingEvent,
		onSuppressWatermark
	});

	return (
		<>{children}</>
	);
};

export default SessionStreamEmbedDataProvider;